import React, { useEffect } from "react";
import auth0 from "auth0-js";
import * as logger from "../../shared/logger";
import * as notify from "../../shared/notify";
import { AuthProvider } from "./authContext";
import { getStorageValue, setStorageValue } from "../../shared/storage";
import { useNavigate } from "react-router-dom";
import { globalEnvironmentConfig } from "../../global-environment-config.js";
import { getPermissions } from "contexts/settings/lib/data";
import { isDev } from "shared/data";

function parseJwt(token) {
    var base64Url = token.split(".")[1];
    var base64 = decodeURIComponent(
        atob(base64Url)
            .split("")
            .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
    );

    return JSON.parse(base64);
}

const auth = new auth0.WebAuth({
    domain: isDev ? process.env.REACT_APP_DOMAIN : globalEnvironmentConfig.Auth0.Domain,
    clientID: isDev ? process.env.REACT_APP_CLIENT_ID : globalEnvironmentConfig.Auth0.ClientId,
    redirectUri: isDev ? process.env.REACT_APP_REDIRECT_URI : globalEnvironmentConfig.Auth0.RedirectUri,
    audience: isDev ? process.env.REACT_APP_AUDIENCE : globalEnvironmentConfig.Auth0.Audience,
    responseType: "token id_token",
    scope: "openid email profile User.Read",
});

const DEFAULT_USER = {
    id: "",
    email: "",
    name: "",
    role: "visitor",
    permissions: [],
    roles: [],
    teams: [],
};

var tokenRenewalTimeout = null;

const Auth = (props) => {
    const navigate = useNavigate();
    const expiresAt = parseInt(localStorage.getItem("expires_at"), 10);
    const token = localStorage.getItem("access_token");
    const isCurrent = new Date().getTime() < expiresAt;
    const user = getStorageValue("user") || DEFAULT_USER;
    const authenticated = token && isCurrent;

    useEffect(() => {
        scheduleRenewal();
    }, []);

    const initiateLogin = () => {
        auth.authorize();
    };

    const logout = (preserveLocation = false) => {
        // Clear access token and ID token from local storage
        localStorage.removeItem("access_token");
        localStorage.removeItem("id_token");
        localStorage.removeItem("expires_at");
        localStorage.removeItem("user");

        if (!preserveLocation) {
            localStorage.removeItem("requested_location");
        }

        if (tokenRenewalTimeout) {
            clearTimeout(tokenRenewalTimeout);
        }

        // navigate to the home route
        window.location = "/";
    };

    const handleAuthentication = () => {
        auth.parseHash((err, authResult) => {
            if (authResult && authResult.accessToken && authResult.idToken) {
                setSession(authResult);
                const requestedLocation = localStorage.getItem("requested_location");
                auth.client.userInfo(authResult.accessToken, async (error, userData) => {
                    const userPermissions = await getPermissions();
                    setStorageValue("user", {
                        ...userData,
                        permissions: userPermissions.permissions,
                        roles: userPermissions.roles,
                        teams: userPermissions.teams,
                    });
                    localStorage.removeItem("requested_location");
                    window.location = requestedLocation || "/";
                });
            } else if (err) {
                logger.log(err);
                logger.log(`Error: ${err.error}. Check the console for further details.`);
                notify.error(`Authentication Error: [${err.errorDescription}]. Please contact a Syndigo administrator or your Intake manager.`);
                navigate("/", { replace: true });
            }
        });
    };

    const setSession = (data) => {
        const userData = {
            id: data.sub,
            email: data.email,
        };
        // Set the time that the access token will expire at
        localStorage.setItem("access_token", data.accessToken);
        localStorage.setItem("id_token", data.idToken);
        localStorage.setItem("expires_at", JSON.stringify(data.expiresIn * 1000 + new Date().getTime()));
        setStorageValue("user", userData);

        scheduleRenewal();
    };

    const renewToken = () => {
        logger.log("Renewing Token");
        auth.checkSession({}, (err, result) => {
            if (err) {
                logger.log(err);
            } else {
                setSession(result);
            }
        });
    };

    const scheduleRenewal = () => {
        const expireTime = getStorageValue("expires_at");
        logger.log(`Token expires: ${new Date(expireTime)}`);
        const expiresIn = expireTime - Date.now();
        const deltaDelay = expiresIn - 600000; // 10 minutes sooner than expire (in milliseconds)

        if (expiresIn > 0) {
            tokenRenewalTimeout = setTimeout(() => {
                renewToken();
            }, deltaDelay);
        }
    };

    const saveRequestedLocation = (location) => {
        localStorage.setItem("requested_location", location);
    };

    const isAllowedPermission = (allowedPermissions) => {
        if (!allowedPermissions || allowedPermissions.length === 0) {
            return true;
        }

        if (!user.permissions || user.permissions.length === 0) {
            return false;
        }

        if (user.permissions.some((perm) => perm.name === allowedPermissions)) {
            return true;
        }

        return false;
    };

    const isAllowedRole = (allowedRoles) => {
        if (!allowedRoles || allowedRoles.length < 1) {
            return true;
        }

        const grantedRoles = parseJwt(token)["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

        if (!grantedRoles) {
            return false;
        }

        // Check roles
        for (var i = 0; i < allowedRoles.length; i++) {
            if (grantedRoles.includes(allowedRoles[i])) {
                return true;
            }
        }

        for (var j = 0; j < grantedRoles.length; j++) {
            if (grantedRoles[j].includes(allowedRoles)) {
                return true;
            }
        }

        return false;
    };

    const authProviderValue = {
        authenticated,
        token,
        user,
        initiateLogin,
        handleAuthentication,
        logout,
        isAllowedPermission,
        isAllowedRole,
        saveRequestedLocation,
        renewToken,
    };

    return <AuthProvider value={authProviderValue}>{props.children}</AuthProvider>;
};

export default Auth;
